import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase, authHelpers } from '../supabaseClient';
import OneSignalService from '../services/OneSignalService';

const AuthContext = createContext({});

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Initialize OneSignal
        const oneSignalAppId = process.env.REACT_APP_ONESIGNAL_APP_ID;
        if (oneSignalAppId) {
            OneSignalService.init(oneSignalAppId);
        }

        // Safety timeout
        const timer = setTimeout(() => {
            setLoading(false);
        }, 5000);

        // Check active session
        supabase.auth.getSession().then(({ data: { session } }) => {
            setUser(session?.user ?? null);
            if (session?.user) {
                loadUserProfile(session.user.id);
            } else {
                setLoading(false);
            }
        }).catch(err => {
            console.error("Session check error", err);
            setLoading(false);
        });

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setUser(session?.user ?? null);
            if (session?.user) {
                loadUserProfile(session.user.id);
            } else {
                setProfile(null);
                setLoading(false);
            }
        });

        return () => {
            subscription.unsubscribe();
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        if (user && process.env.REACT_APP_ONESIGNAL_APP_ID) {
            OneSignalService.login(user.id);
        }
    }, [user]);

    const loadUserProfile = async (userId) => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (error) throw error;
            setProfile(data);
        } catch (error) {
            console.error('Error loading profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const signIn = async (email, password) => {
        const { data, error } = await authHelpers.signIn(email, password);
        if (!error && data.user) {
            await loadUserProfile(data.user.id);
        }
        return { data, error };
    };

    const signUp = async (email, password, userData = {}) => {
        const { data, error } = await authHelpers.signUp(email, password, userData);

        // If signup successful, CREATE profile in profiles table
        if (!error && data?.user) {
            try {
                const fullName = userData.full_name ||
                    `${userData.first_name || ''} ${userData.last_name || ''}`.trim() ||
                    email.split('@')[0];

                // Use upsert to create profile (columns: id, email, full_name, role)
                const profileRole = userData.role || 'client';
                const { error: profileError } = await supabase
                    .from('profiles')
                    .upsert([{
                        id: data.user.id,
                        email: email,
                        full_name: fullName,
                        role: profileRole
                    }], { onConflict: 'id' });

                if (profileError) {
                    console.error('Profile creation error:', profileError);
                }
            } catch (profileError) {
                console.error('Profile creation exception:', profileError);
            }
        }

        return { data, error };
    };

    const signOut = async () => {
        try {
            await authHelpers.signOut();
            if (process.env.REACT_APP_ONESIGNAL_APP_ID) {
                OneSignalService.logout();
            }
        } catch (err) {
            console.error("SignOut error:", err);
        }
        // Always clear local session even if the server returns an error (e.g. session already expired)
        setUser(null);
        setProfile(null);
    };

    const value = {
        user,
        profile,
        loading,
        signIn,
        signUp,
        signOut,
        isAdmin: profile?.role === 'admin',
        isClient: profile?.role === 'client',
        isProducer: profile?.role === 'producteur'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
